import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { getPeriodRanges } from '../../utils/periodUtils';
import MetricCard from '../../components/dashboard/MetricCard';
import PaymentRevenueChart from '../../components/charts/PaymentRevenueChart';
import { usePaymentRevenueData } from '../../hooks/usePaymentRevenueData';
import { PAYMENTS_DUE_STATUS_UPDATED } from '../../hooks/usePaymentDueStatusSync';
import { Lead, Payment, Project } from '../../types';
import { getRevenueAmount } from '../../utils/paymentRevenue';
import PipelineOverview from '../../components/dashboard/PipelineOverview';
import RecentProjects from '../../components/dashboard/RecentProjects';
import RightsWatchCard from '../../components/dashboard/RightsWatchCard';
import UrgentFollowupsCard from '../../components/dashboard/UrgentFollowupsCard';
import HighlightsBoard from '../../components/dashboard/HighlightsBoard';
// Calendar moved to its own page
import { leadsService, paymentsService, profilesService, projectsService } from '../../services/supabase';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { blockDemoWrite, isDemoMode } from '../../services/dataScope';
import { 
  Users, 
  FolderOpen, 
  TrendingUp,
} from 'lucide-react';
import Card from '../../components/ui/Card';
import PeriodToggle, { PeriodValue } from '../../components/ui/PeriodToggle';
import PageHeader from '../../components/ui/PageHeader';
import { PAGE_STACK, PAGE_GRID_GAP } from '../../styles/pageLayout';

const firstNameFrom = (raw: string | null | undefined): string => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0] || '';
};

const DASHBOARD_CHART_MIN_H =
  'min-h-[260px] lg:min-h-[360px] xl:min-h-[420px] [@media(min-height:900px)]:xl:min-h-[560px]';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, showToastWithUndo } = useToast();
  // Core dashboard data (avoid full-table fetches)
  const [payments, setPayments] = useState<Payment[]>([]);
  const [urgentTasks, setUrgentTasks] = useState<Lead[]>([]);
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [rightsToWatch, setRightsToWatch] = useState<Project[]>([]);
  const [checkingRightsId, setCheckingRightsId] = useState<string | null>(null);
  const [recentLeads, setRecentLeads] = useState<Lead[]>([]);
  const [leadsByStatusThis, setLeadsByStatusThis] = useState<Record<string, number>>({});
  const [leadsByStatusPrev, setLeadsByStatusPrev] = useState<Record<string, number>>({});
  const [projectsTotalThis, setProjectsTotalThis] = useState(0);
  const [projectsTotalPrev, setProjectsTotalPrev] = useState(0);
  const [projectsArchivedThis, setProjectsArchivedThis] = useState(0);
  const [pipelineConversionRate, setPipelineConversionRate] = useState(0);
  // Highlights: only entities linked to paid payments (not full tables)
  const [statsLeads, setStatsLeads] = useState<Lead[]>([]);
  const [statsProjects, setStatsProjects] = useState<Project[]>([]);
  const [hasAnyLeads, setHasAnyLeads] = useState(false);
  const [hasAnyProjects, setHasAnyProjects] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileFirstName, setProfileFirstName] = useState('');
  const { t, language } = useI18n();
  const { formatCurrency } = useTranslations();
  // Calendar moved to its own page; only keep period toggle here.
  const [period, setPeriod] = useState<PeriodValue>('year');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const profile = await profilesService.getProfile();
      if (cancelled) return;
      const fromProfile = typeof profile?.name === 'string' ? profile.name : '';
      const meta = user?.user_metadata || {};
      const fromMeta =
        (typeof meta.name === 'string' && meta.name) ||
        (typeof meta.full_name === 'string' && meta.full_name) ||
        '';
      setProfileFirstName(firstNameFrom(fromProfile || fromMeta));
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const dashboardTitle = profileFirstName
    ? t('dashboard.greetingHelloName', { name: profileFirstName })
    : t('dashboard.greetingHello');

  // Get payment revenue data for the chart, driven by period
  const mode = period === 'month' ? 'month' : period === 'year' ? 'year' : 'total';
  const { data: revenueData, loading: revenueLoading } = usePaymentRevenueData({ monthsToShow: 15, mode, payments });

  // Payments + slim highlights (projects/leads referenced by paid payments) + empty-CTA counts
  useEffect(() => {
    const abortController = new AbortController();

    const loadPaymentsAndHighlights = async (paymentsData: Payment[]) => {
      const paidProjectIds = Array.from(
        new Set(
          paymentsData
            .filter((p) => p.status === 'paid' && p.projectId)
            .map((p) => p.projectId)
        )
      );
      const projects = await projectsService.getByIds(paidProjectIds);
      if (abortController.signal.aborted) return;
      setStatsProjects(projects);

      const leadIds = Array.from(new Set(projects.map((p) => p.leadId).filter(Boolean)));
      const leads = await leadsService.getByIds(leadIds);
      if (abortController.signal.aborted) return;
      setStatsLeads(leads);
    };

    const load = async () => {
      try {
        setStatsLoading(true);
        const [paymentsData, leadsCount, projectsCount] = await Promise.all([
          paymentsService.getAll(),
          leadsService.countAll(),
          projectsService.countAll(),
        ]);
        if (abortController.signal.aborted) return;
        setPayments(paymentsData);
        setHasAnyLeads(leadsCount > 0);
        setHasAnyProjects(projectsCount > 0);
        await loadPaymentsAndHighlights(paymentsData);
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error loading payments/highlights:', error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setStatsLoading(false);
        }
      }
    };

    void load();

    return () => {
      abortController.abort();
    };
  }, []);

  // Refresh payments (+ highlight entities) when due status sync runs (from Layout)
  useEffect(() => {
    const handler = () => {
      void (async () => {
        try {
          const paymentsData = await paymentsService.getAll();
          setPayments(paymentsData);
          const paidProjectIds = Array.from(
            new Set(
              paymentsData
                .filter((p) => p.status === 'paid' && p.projectId)
                .map((p) => p.projectId)
            )
          );
          const projects = await projectsService.getByIds(paidProjectIds);
          setStatsProjects(projects);
          const leadIds = Array.from(new Set(projects.map((p) => p.leadId).filter(Boolean)));
          const leads = await leadsService.getByIds(leadIds);
          setStatsLeads(leads);
        } catch (e) {
          console.error(e);
        }
      })();
    };
    window.addEventListener(PAYMENTS_DUE_STATUS_UPDATED, handler);
    return () => window.removeEventListener(PAYMENTS_DUE_STATUS_UPDATED, handler);
  }, []);

  // Load dashboard core data (best practice: targeted queries + small lists)
  useEffect(() => {
    const abortController = new AbortController();

    const loadCore = async () => {
      try {
        setLoading(true);
        setError(null);

        const { start, end, prevStart, prevEnd } = getPeriodRanges(period, language);

        if (period === 'total') {
          const [
            urgent,
            recent,
            rightsWatch,
            statusAll,
            projectsTotal,
            archivedTotal,
          ] = await Promise.all([
            leadsService.getUrgentFollowups(12),
            projectsService.getRecent(12),
            projectsService.getRightsToWatch(12),
            leadsService.getStatusCountsAll(),
            projectsService.countAll(),
            projectsService.countAll({ statusEq: 'ARCHIVED' }),
          ]);

          if (abortController.signal.aborted) return;

          setUrgentTasks(urgent);
          setRecentProjects(recent);
          setRightsToWatch(rightsWatch);
          setLeadsByStatusThis(statusAll);
          setLeadsByStatusPrev({});
          setProjectsTotalThis(projectsTotal);
          setProjectsTotalPrev(0);
          setProjectsArchivedThis(archivedTotal);

          // Load leads for recent projects only (small)
          const leadIds = Array.from(new Set(recent.map((p) => p.leadId).filter(Boolean)));
          const leadsForRecent = await leadsService.getByIds(leadIds);
          if (!abortController.signal.aborted) setRecentLeads(leadsForRecent);

          const leadsCountAll = Object.values(statusAll).reduce((a, b) => a + b, 0);
          const convertedAll = (statusAll['in_production'] || 0) + (statusAll['completed'] || 0);
          const conv = leadsCountAll > 0 ? Math.min(100, (convertedAll / leadsCountAll) * 100) : 0;
          setPipelineConversionRate(Math.round(conv));
        } else {
          const [
            urgent,
            recent,
            rightsWatch,
            statusThis,
            statusPrev,
            projectsActiveThis,
            projectsArchivedThisCount,
            projectsActivePrev,
            projectsArchivedPrev,
          ] = await Promise.all([
            leadsService.getUrgentFollowups(12),
            projectsService.getRecent(12),
            projectsService.getRightsToWatch(12),
            leadsService.getStatusCountsByLastActionInRange(start, end),
            leadsService.getStatusCountsByLastActionInRange(prevStart, prevEnd),
            projectsService.countByCreatedAtInRange(start, end, { statusNeq: 'ARCHIVED' }),
            projectsService.countByCreatedAtInRange(start, end, { statusEq: 'ARCHIVED' }),
            projectsService.countByCreatedAtInRange(prevStart, prevEnd, { statusNeq: 'ARCHIVED' }),
            projectsService.countByCreatedAtInRange(prevStart, prevEnd, { statusEq: 'ARCHIVED' }),
          ]);

          if (abortController.signal.aborted) return;

          setUrgentTasks(urgent);
          setRecentProjects(recent);
          setRightsToWatch(rightsWatch);
          setLeadsByStatusThis(statusThis);
          setLeadsByStatusPrev(statusPrev);
          setProjectsTotalThis(projectsActiveThis + projectsArchivedThisCount);
          setProjectsTotalPrev(projectsActivePrev + projectsArchivedPrev);
          setProjectsArchivedThis(projectsArchivedThisCount);

          // Load leads for recent projects only (small)
          const leadIds = Array.from(new Set(recent.map((p) => p.leadId).filter(Boolean)));
          const leadsForRecent = await leadsService.getByIds(leadIds);
          if (!abortController.signal.aborted) setRecentLeads(leadsForRecent);

          const leadsCountThis = Object.values(statusThis).reduce((a, b) => a + b, 0);
          const convertedThis = (statusThis['in_production'] || 0) + (statusThis['completed'] || 0);
          const conv = leadsCountThis > 0 ? Math.min(100, (convertedThis / leadsCountThis) * 100) : 0;
          setPipelineConversionRate(Math.round(conv));
        }
      } catch (e) {
        console.error('Error loading dashboard:', e);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    };

    loadCore();
    return () => abortController.abort();
  }, [period, language]);


  if (error) {
    return (
      <div className={PAGE_STACK}>
        <PageHeader
          title={dashboardTitle}
          description={t('dashboard.subtitle')}
          actionsConfig={{
            customActions: <PeriodToggle value={period} onChange={setPeriod} size="sm" showRange />
          }}
        />
        <Card>
          <div className="py-12 text-center">
            <p className="text-red-600 font-medium">{t('common.error')}</p>
            <p className="text-gray-500 mt-1">{error}</p>
          </div>
        </Card>
      </div>
    );
  }

  // Removed overview-only helper functions to avoid unused code

  const { start, end, prevStart, prevEnd, comparisonDate } = getPeriodRanges(period, language);

  const pctChange = (current: number, prev: number): { value: number; trend: 'up' | 'down' | 'neutral' } => {
    if (period === 'total') return { value: 0, trend: 'neutral' };
    if (prev === 0) {
      if (current === 0) return { value: 0, trend: 'neutral' };
      return { value: 100, trend: 'up' };
    }
    const delta = ((current - prev) / prev) * 100;
    return { value: Math.round(delta), trend: delta > 0 ? 'up' : delta < 0 ? 'down' : 'neutral' };
  };

  const changeLabel = period !== 'total' 
    ? `vs ${comparisonDate}` 
    : '';
  const showChange = period !== 'total';

  const leadsCountThis = Object.values(leadsByStatusThis).reduce((a, b) => a + b, 0);
  const leadsCountPrev = Object.values(leadsByStatusPrev).reduce((a, b) => a + b, 0);

  const pipelineLeadsByStatus = leadsByStatusThis;

  // Data already shaped by hook; no additional filtering needed
  const filteredRevenueData = revenueData;

  const activeProjectsCount = projectsTotalThis - projectsArchivedThis;
  const archivedProjectsCount = projectsArchivedThis;
  const totalProjectsThis = projectsTotalThis;
  const totalProjectsPrev = projectsTotalPrev;
  const projectsChange = pctChange(totalProjectsThis, totalProjectsPrev);

  // Finance metrics: simple addition logic
  // Payé: payments with status 'paid'
  // En attente: ALL payments with status 'sent' or 'overdue' (including future payments)
  // Note: Uses amountBaseCurrency (in user's base currency) for consistent statistics across all currencies
  const relevantPayments = period === 'total'
    ? payments
    : payments.filter(inv => {
        if (inv.status === 'paid' && inv.paymentDate) {
          const paymentDate = new Date(inv.paymentDate);
          return paymentDate >= start && paymentDate <= end;
        } else if (inv.status === 'sent' || inv.status === 'overdue') {
          const dueDate = new Date(inv.dueDate);
          return dueDate >= start && dueDate <= end;
        }
        return false;
      });
  
  const paidPayments = relevantPayments.filter(inv => inv.status === 'paid');
  const pendingPayments = relevantPayments.filter(inv => inv.status === 'sent' || inv.status === 'overdue');
  
  // Sum amounts using amountBaseCurrency (in user's base currency) for consistent statistics
  const paidAmount = paidPayments.reduce((sum, inv) => sum + getRevenueAmount(inv), 0);
  
  const paidAmountPrev = payments
    .filter(inv => inv.status === 'paid' && inv.paymentDate && new Date(inv.paymentDate) >= prevStart && new Date(inv.paymentDate) <= prevEnd)
    .reduce((sum, inv) => {
      return sum + getRevenueAmount(inv);
    }, 0);
  const financeChange = pctChange(paidAmount, paidAmountPrev);

  // En attente: ALL sent + overdue (including future payments)
  // Note: In mode 'total', we include ALL pending payments regardless of dueDate
  const expectedAmount = pendingPayments.reduce((sum, inv) => sum + getRevenueAmount(inv), 0);
  
  // Chiffre principal = simple addition des subvalues
  const totalRevenue = paidAmount + expectedAmount;

  const showFirstLeadCta = !isDemoMode() && !statsLoading && !hasAnyLeads;
  const showFirstProjectCta = !isDemoMode() && !statsLoading && !hasAnyProjects;

  return (
    <div className={`w-full ${PAGE_STACK}`}>
      {/* Header */}
      <PageHeader
        title={dashboardTitle}
        description={t('dashboard.subtitle')}
        actionsConfig={{
          customActions: <PeriodToggle value={period} onChange={setPeriod} size="sm" showRange />
        }}
      />

      {/* Main Metrics - full width across three columns */}
      <div className={`grid grid-cols-1 xl:grid-cols-3 ${PAGE_GRID_GAP} items-stretch`}>
        <MetricCard
          title={t('leads.title')}
          value={leadsCountThis}
          icon={Users}
          color="orange"
          loading={loading}
          change={showChange ? {
            value: pctChange(leadsCountThis, leadsCountPrev).value,
            label: changeLabel,
            trend: pctChange(leadsCountThis, leadsCountPrev).trend
          } : undefined}
          onClick={isDemoMode() ? undefined : () => navigate('/app/leads')}
          ariaLabel={t('leads.title')}
          subValues={[
            { label: t('status.rejected'), value: loading ? 0 : (function(){
              const statusesRejected = ['rejected','abandoned'];
              return Object.entries(leadsByStatusThis).filter(([s]) => statusesRejected.includes(s)).reduce((acc, [,v]) => acc + v, 0);
            })() },
            { label: t('status.inProgress'), value: loading ? 0 : (function(){
              const statusesInProgress = ['to_contact','pending','negotiation','in_production'];
              return Object.entries(leadsByStatusThis).filter(([s]) => statusesInProgress.includes(s)).reduce((acc, [,v]) => acc + v, 0);
            })() },
            { label: t('status.completed'), value: loading ? 0 : (leadsByStatusThis['completed'] || 0) },
          ]}
        />
        <MetricCard
          title={t('projects.title')}
          value={totalProjectsThis}
          icon={FolderOpen}
          color="blue"
          loading={loading}
          subValues={[
            { label: t('status.inProgress'), value: activeProjectsCount },
            { label: (t('projects.archived') || '').replace(/^./, (c) => c.toUpperCase()), value: archivedProjectsCount },
          ]}
          change={showChange ? {
            value: projectsChange.value,
            label: changeLabel,
            trend: projectsChange.trend
          } : undefined}
          onClick={isDemoMode() ? undefined : () => navigate('/app/projects')}
          ariaLabel={t('projects.title')}
        />
        <MetricCard
          title={t('finances.revenue')}
          value={formatCurrency(totalRevenue)}
          icon={TrendingUp}
          color="green"
          loading={loading}
          change={showChange ? {
            value: financeChange.value,
            label: changeLabel,
            trend: financeChange.trend
          } : undefined}
          subValues={[
            { label: t('status.paid'), value: formatCurrency(paidAmount) },
            { label: t('finances.pending'), value: formatCurrency(expectedAmount) },
          ]}
          onClick={isDemoMode() ? undefined : () => navigate('/app/finances')}
          ariaLabel={t('finances.title')}
        />
      </div>

      {/* Urgent actions / Rights watch / Recent projects */}
      <div className={`grid grid-cols-1 xl:grid-cols-3 ${PAGE_GRID_GAP} items-stretch`}>
            {/* Actions urgentes */}
            <div className="flex flex-col min-h-[140px] lg:min-h-[180px] [@media(min-height:900px)]:lg:min-h-[220px]">
              <UrgentFollowupsCard
                leads={urgentTasks}
                showFirstLeadCta={showFirstLeadCta}
                onCreateFirstLead={() => {
                  if (blockDemoWrite()) return;
                  navigate('/app/leads?create=1');
                }}
              />
            </div>

            {/* Droits d'image */}
            <div className="flex flex-col min-h-[140px] lg:min-h-[180px] [@media(min-height:900px)]:lg:min-h-[220px]">
              <RightsWatchCard
                projects={rightsToWatch}
                checkingId={checkingRightsId}
                onMarkChecked={async (projectId) => {
                  if (blockDemoWrite()) return;

                  const project = rightsToWatch.find((p) => p.id === projectId);
                  if (!project) return;

                  setCheckingRightsId(projectId);
                  setRightsToWatch((prev) => prev.filter((p) => p.id !== projectId));
                  const ok = await projectsService.markRightsChecked(projectId);
                  setCheckingRightsId(null);

                  if (!ok) {
                    const refreshed = await projectsService.getRightsToWatch(12);
                    setRightsToWatch(refreshed);
                    showToast(t('dashboard.rightsMarkCheckedError'), 'error');
                    return;
                  }

                  showToastWithUndo(
                    t('dashboard.rightsMarkedChecked', { name: project.name }),
                    async () => {
                      await projectsService.unmarkRightsChecked(projectId);
                      const restored = await projectsService.getRightsToWatch(12);
                      setRightsToWatch(restored);
                    },
                    'success'
                  );

                  const refreshed = await projectsService.getRightsToWatch(12);
                  setRightsToWatch(refreshed);
                }}
              />
            </div>

            {/* Projets récents */}
            <div className="flex flex-col min-h-[140px] lg:min-h-[180px] [@media(min-height:900px)]:lg:min-h-[220px]">
              <RecentProjects
                projects={recentProjects}
                leads={recentLeads}
                payments={payments}
                showFirstProjectCta={showFirstProjectCta}
                onCreateFirstProject={() => {
                  if (blockDemoWrite()) return;
                  navigate('/app/projects?create=1');
                }}
              />
            </div>
      </div>

      {/* Charts (equal height) + top leads/projects underneath each column */}
      <div className={`grid grid-cols-1 xl:grid-cols-2 ${PAGE_GRID_GAP}`}>
        <div className={`relative flex flex-col ${DASHBOARD_CHART_MIN_H}`}>
          <div
            className={`flex-1 transition-opacity duration-300 ${
              revenueLoading ? 'opacity-50' : 'opacity-100'
            }`}
          >
            <PaymentRevenueChart
              data={filteredRevenueData}
              className="h-full"
              fillHeight
            />
          </div>
        </div>

        <div className={`relative flex flex-col ${DASHBOARD_CHART_MIN_H}`}>
          <div className="flex-1">
            <PipelineOverview
              leadsByStatus={pipelineLeadsByStatus}
              conversionRate={pipelineConversionRate}
              showFooter
              className="h-full"
              minHeightClass="min-h-0"
            />
          </div>
        </div>

        <div className="min-h-0">
          {!statsLoading ? (
            <HighlightsBoard
              leads={statsLeads}
              projects={statsProjects}
              payments={payments}
              period={period}
              section="leads"
            />
          ) : null}
        </div>

        <div className="min-h-0">
          {!statsLoading ? (
            <HighlightsBoard
              leads={statsLeads}
              projects={statsProjects}
              payments={payments}
              period={period}
              section="projects"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
